import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsTreePivot } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'

import { ANALYTICS_CAPTIONS } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { getCurrentDate } from '@/helpers/dates'
import { getFilterTags, getFilterValues } from '../utils/helpers'
import { prepareTagsList } from '@/views/Dashboards/utils/helpers'

import useAnalyticsStore from '../index'
import { useTagsBunchStore } from '@/store/tags/bunch'

import { DuckDBService } from '@/services/duckdb'

export const TABLE_NAME = 'analytics_tree_pivot'
export const TREE_PIVOT_VALUE_FIELD = 'value'
// The view renames position_close to value, but the caption is the same one every
// other analytics table uses.
export const TREE_PIVOT_VALUE_CAPTION = ANALYTICS_CAPTIONS[AnalyticsField.POSITION_CLOSE]
// Cost and gain keep their shared field names but not the shared captions: the Value
// table calls them Cost and Gain, and the Tree Pivot spells them out.
export const TREE_PIVOT_COST_CAPTION = 'Cost Basis'
export const TREE_PIVOT_GAIN_CAPTION = 'Unrealized Gain or Loss'
export const getTreePivotTagCaption = (name: string) => `Tag: ${name}`

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  [TREE_PIVOT_VALUE_FIELD]: 'DOUBLE',
  [AnalyticsField.POSITION_COST]: 'DOUBLE',
  [AnalyticsField.POSITION_GAIN]: 'DOUBLE',
  [AnalyticsField.TAGS]: 'VARCHAR[]',
}

const useAnalyticsTreePivotStore = defineStore('analytics-tree-pivot', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const analyticsStore = useAnalyticsStore()

  const list = ref<AnalyticsTreePivot[]>([])

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  // GETTERS
  const isInit = computed(() => deprecated.value !== undefined)

  const getList = computed(() => {
    if (analyticsStore.getError) return []
    return list.value
  })

  // Feeds the shared timeline so its arrows have a range to step through, the same way
  // the value/income/position stores do.
  const getDatesRange = computed(() => [...new Set(list.value.map(item => item.date))].sort())

  // SETTERS
  const setList = async (data: { tree_pivot_view: AnalyticsTreePivot[] }) => {
    if (deprecated.value === false) return
    if (!data.tree_pivot_view) return
    list.value = data.tree_pivot_view
    deprecated.value = false
    // The view already emits tag ids, so rows go in as they arrive and pushData picks
    // out the schema columns.
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    if (list.value.length) {
      await duckdbInstance.pushData(TABLE_NAME, list.value, TABLE_SCHEMA)
    }
    trigger.value++
  }

  // ACTIONS
  const clear = () => {
    list.value = []
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getDatesRange,
    getList,

    setList,

    clear,
  }
})

export default useAnalyticsTreePivotStore

export const useAnalyticsTreePivotFilters = () => {
  const analyticsStore = useAnalyticsStore()
  const tagsBunchStore = useTagsBunchStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))
  const tagNames = computed(() => Object.keys(prepareTagsList(tagsBunchStore.getCommonList, true)))

  const getValues = getFilterValues(TABLE_NAME, analyticsDate, true, tagsBunchStore)
  const getTags = getFilterTags(TABLE_NAME, analyticsDate, tagsBunchStore, true)

  const filters = computed(() => {
    const result: FilterField[] = []
    result.push(
      {
        name: AnalyticsField.ACCOUNT_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT],
        getter: (context, comparison) => getValues(AnalyticsField.ACCOUNT_ID, undefined, context, comparison),
      },
      {
        name: AnalyticsField.ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_TYPE],
        getter: (context, comparison) => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.TYPE, context, comparison),
      },
      {
        name: AnalyticsField.ASSET_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET],
        getter: (context, comparison) => getValues(AnalyticsField.ASSET_ID, undefined, context, comparison),
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: (context, comparison) => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE, context, comparison),
      },
      {
        name: TREE_PIVOT_VALUE_FIELD,
        caption: TREE_PIVOT_VALUE_CAPTION,
        isAmount: true,
      },
      {
        name: AnalyticsField.POSITION_COST,
        caption: TREE_PIVOT_COST_CAPTION,
        isAmount: true,
      },
      {
        name: AnalyticsField.POSITION_GAIN,
        caption: TREE_PIVOT_GAIN_CAPTION,
        isAmount: true,
      },
    )
    tagNames.value.forEach(name => {
      result.push({
        name,
        caption: getTreePivotTagCaption(name),
        getter: (context, comparison) => getTags(name, context, comparison),
      })
    })
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
